import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { GetPrivateRegionLeasedTimeMetricsRes } from '@/api/type-response'
import { message } from '@/components/base/Notification'
import { PrivateRegionNodeType } from '@/consts/privateRegion'

export function useUsageMetrics(selectedRegion: number | null, selectedModel?: string | number) {
  const { t } = useTranslation('privateRegion')
  const [usageMetrics, setUsageMetrics] = useState<GetPrivateRegionLeasedTimeMetricsRes | null>(
    null,
  )
  const [loading, setLoading] = useState(false)

  const loadData = useCallback(
    async (signal: AbortSignal) => {
      if (selectedRegion === null) return

      setLoading(true)
      try {
        // Fixed time range: this month (30 days)
        const now = dayjs()
        const startTime = now.startOf('month')
        const endTime = now.endOf('month')

        const metrics = await API.getPrivateRegionLeasedTimeMetrics(
          {
            region: selectedRegion,
            type: PrivateRegionNodeType.AllNode,
            start_time: startTime.format(),
            end_time: endTime.format(),
            model: selectedModel ? String(selectedModel) : undefined,
          },
          { signal },
        )

        // Set empty metrics array if API fails, so UI can still render
        setUsageMetrics(metrics.code === 0 ? metrics : { metrics: [] })
      } catch (error: any) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error.name === 'CanceledError') return
        message.error(t('errors.failedToLoadUsageMetrics'))
      } finally {
        setLoading(false)
      }
    },
    [selectedRegion, selectedModel, t],
  )

  useEffect(() => {
    if (selectedRegion === null) return

    const abortController = new AbortController()
    loadData(abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [selectedRegion, loadData])

  const refetch = useCallback(() => {
    if (selectedRegion !== null) {
      const abortController = new AbortController()
      loadData(abortController.signal)
    }
  }, [selectedRegion, loadData])

  return {
    usageMetrics,
    loading,
    refetch,
  }
}
